/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Card, CardExtra } from '../../../Shared/Card'
import { Icon } from 'semantic-ui-react'
import { Link } from 'react-router-dom-v5-compat'
import React from 'react'
import { getMissedMetrics } from '../../../../../services/Metrics/Metrics.service'
import { getCancellationSource } from '../../../../../services/http-client'
import { isProviderAPos } from '../../../../../services/Provider/Provider.service'
import { formatISO, roundToNearestMinutes, startOfMonth } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import useUser from '../../../../Shared/UserProvider/useUser'
import { useUnavailableItemSummary } from '../../../../Home/UnavailableItems/hooks'
import { LoadingPlaceholderCardContent } from '../../../Shared/LoadingPlaceholderCardContent'
import { THEME_COLORS } from '../../../../Shared/theme'
import { useDeviceMediaQuery } from '../../../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { MissedRevenueCardContent } from './MissedRevenueCardContent'
import { NoMissedRevenueCardContent } from './NoMissedRevenueCardContent'
import { useTranslation } from 'react-i18next'


const MissedRevenueCard = () => {
    const [t] = useTranslation()
    const {isMobile} = useDeviceMediaQuery()
    const {user} = useUser()
    const from = formatISO(startOfMonth(new Date()))
    const to = formatISO(roundToNearestMinutes(new Date(), {nearestTo: 10, roundingMethod: 'floor'}))

    const {data: negativeData, isLoading: isNegativeLoading} = useQuery({
        queryKey: ['negative-metrics', from, to],
        queryFn: async () => await getMissedMetrics({
            dateRange: {startDate: from, endDate: to},
            merchantId: isProviderAPos(user.provider) ? user.provider.object.id : null,
            brandIds: [],
            platformIds: [],
        }, getCancellationSource()),
    })

    const {data: summary, isLoading: isSummaryLoading} = useUnavailableItemSummary({from, to})

    const isLoading = isNegativeLoading || isSummaryLoading

    const metrics = negativeData?.byDate.reduce((total, metric) => {
        total.revenuesMissed = (total.revenuesMissed || 0) + (metric.revenuesMissed || 0)
        total.missedorder = (total.missedorder || 0) + (metric.missedorder || 0)
        return total
    }, {})
    const revenuesMissed = metrics?.revenuesMissed || 0
    const missedOrder = metrics?.missedorder || 0

    const totalMissedRevenueSubstituted = summary?.totalMissedRevenueSubstituted || 0
    const totalMissedRevenueUnavailable = (summary?.totalMissedRevenueUnavailable || 0) - totalMissedRevenueSubstituted
    const totalMissedQuantity = summary?.totalMissedQuantity || 0

    const totalMissedRevenue = revenuesMissed + totalMissedRevenueUnavailable

    const noNegativeActivity = revenuesMissed === 0 && missedOrder === 0
    const noUnavailableItems = totalMissedQuantity === 0
    const noActivity = noNegativeActivity && noUnavailableItems

    const getBorderColor = () => {
        if (isLoading) return 'unset'
        return noActivity ? THEME_COLORS.GREEN : THEME_COLORS.RED
    }

    return (
      <Card
        css={css`
            border-top: 2px solid ${getBorderColor()};

            .card-content {
                width: ${isMobile ? '340px' : '360px'};
                height: 402px;
            }
        `}
      >
          {isLoading ? (
            <LoadingPlaceholderCardContent/>
          ) : noActivity ? (
            <NoMissedRevenueCardContent/>
          ) : (
            <MissedRevenueCardContent
              from={from}
              to={to}
              totalMissedRevenue={totalMissedRevenue}
              revenuesMissed={revenuesMissed}
              totalMissedRevenueUnavailable={totalMissedRevenueUnavailable}
              totalMissedRevenueSubstituted={totalMissedRevenueSubstituted}
              totalMissedQuantity={totalMissedQuantity}
              totalMissedSubstitutedQuantity={summary?.totalMissedSubstitutedQuantity || 0}
            />
          )}
          <CardExtra>
              <Link
                to={{
                    pathname: '/analytics/missed',
                    search: new URLSearchParams({from, to}).toString(),
                }}
              >
                  {t('dashboard.missed_revenues.missed_revenues')}
                  <Icon name="arrow right"/>
              </Link>
          </CardExtra>
      </Card>
    )
}

export { MissedRevenueCard }
